// =============================================================================
// ScheduleTab — per-location opening hours editor (post-0048)
// =============================================================================
// One location at a time (driven by the global location switcher). Each rule
// card defines: weekday selection + date range + one or more open periods.
// Inserting an "Add break" splits the open window into two periods separated
// by a closed gap.
//
// Slot generation cadence is fixed at 20 min in fn_availability (0051) and
// resets per period; the last bookable start in a period is
// (closes_at − experience.duration_minutes).
//
// Override rules ("Vervang andere regels op deze datums", is_override) replace
// every other rule on the dates they cover; zero periods = closed. They are
// date-driven, so the weekday set is DERIVED from the date range (single date
// → that weekday; range → every day in it) unless the operator opens the
// "Alleen bepaalde weekdagen" pattern. See lib/schedule-rules.ts.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { formatError } from '../../lib/errors';
import { fetchWalkinRefData, type BookingLocation } from '../../lib/walkin-data';
import {
  getLocationSchedule,
  saveLocationSchedule,
  type LocationScheduleRule,
  type LocationScheduleRuleInput,
} from '../../lib/setup';
import { useLocationFilter } from '../../lib/location-context';
import {
  ALL_WEEKDAYS,
  effectiveDays,
  formatDutchDate,
  isCustomDayPattern,
  ruleNeverMatches,
  weekdaysInRange,
} from '../../lib/schedule-rules';

const WEEKDAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'] as const;
const WEEKDAY_ISO   = [1, 2, 3, 4, 5, 6, 7] as const;

interface DraftPeriod {
  opens_at:  string; // 'HH:MM'
  closes_at: string;
}
interface DraftRule {
  draftId:    string;
  days:       Set<number>;
  validFrom:  string;
  validUntil: string;
  isOverride: boolean;
  // Override only: the operator picked a weekday pattern by hand. Otherwise
  // the saved day_of_week is derived from the date range (effectiveDays).
  customDays: boolean;
  periods:    DraftPeriod[];
}

let draftIdCounter = 0;
const newDraftId = () => `draft-${++draftIdCounter}`;

function snapshotToDrafts(rules: LocationScheduleRule[]): DraftRule[] {
  return rules.map((r) => {
    const isOverride = r.is_override ?? false;
    const days = new Set(r.day_of_week ?? []);
    return {
      draftId:    r.id,
      days,
      validFrom:  r.valid_from,
      validUntil: r.valid_until,
      isOverride,
      customDays: isOverride && isCustomDayPattern(days, r.valid_from, r.valid_until),
      // Preserve 0-period rules iff they're overrides (closure semantics).
      // Non-override rules with 0 periods are meaningless — coerce to a
      // sane default so the editor isn't blank.
      periods:    r.periods.length > 0
        ? r.periods.map((p) => ({ opens_at: p.opens_at, closes_at: p.closes_at }))
        : (isOverride ? [] : [{ opens_at: '12:00', closes_at: '17:40' }]),
    };
  });
}

// ---- Time helpers ----------------------------------------------------------
const toMins = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const fmtDuration = (totalMins: number): string => {
  if (totalMins <= 0) return '0 min';
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} u`;
  return `${h} u ${m} min`;
};
const ruleOpenMinutes = (r: DraftRule): number =>
  r.periods.reduce((sum, p) => sum + Math.max(0, toMins(p.closes_at) - toMins(p.opens_at)), 0);

export function ScheduleTab() {
  const { operator } = useAuth();
  const { locationId: globalLocationId, setLocationId: setGlobalLocationId } =
    useLocationFilter();

  const [locations, setLocations]   = useState<BookingLocation[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError]     = useState<string | null>(null);

  const [locationId, setLocationId] = useState<string>('');

  const [drafts, setDrafts]         = useState<DraftRule[]>([]);
  // Which location `drafts` actually belong to. Saving is a replace-all that
  // wipes the target location's rules, so we refuse to save unless the
  // on-screen drafts were loaded for the currently-selected location — this
  // closes the gap where a mid-switch save overwrote one location with
  // another's schedule (the prod cross-contamination bug).
  const [loadedLocationId, setLoadedLocationId] = useState<string | null>(null);
  // Latest selected location, read inside async reloads to drop stale resolves.
  const latestLocationRef = useRef<string>('');
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError]   = useState<string | null>(null);

  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash]   = useState(false);

  // ---- Initial load ------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefLoading(true);
      setRefError(null);
      try {
        const ref = await fetchWalkinRefData(true);
        if (cancelled) return;
        setLocations(ref.locations);
        setLocationId(globalLocationId ?? ref.locations[0]?.id ?? '');
      } catch (e) {
        if (!cancelled) setRefError(formatError(e));
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the ref pointed at the current selection so an in-flight reload can
  // detect the selection changed under it and skip applying stale data.
  useEffect(() => {
    latestLocationRef.current = locationId;
  }, [locationId]);

  // Honor global location switcher selection.
  useEffect(() => {
    if (globalLocationId && globalLocationId !== locationId) {
      setLocationId(globalLocationId);
    }
  }, [globalLocationId, locationId]);

  // ---- Load schedule when location changes -------------------------------
  const reload = useCallback(async () => {
    if (!locationId) {
      setDrafts([]);
      setLoadedLocationId(null);
      return;
    }
    const target = locationId;
    setSnapLoading(true);
    setSnapError(null);
    // Invalidate immediately: until the matching schedule lands there are no
    // drafts that may be saved against `target`.
    setLoadedLocationId(null);
    setDrafts([]);
    try {
      const s = await getLocationSchedule(target);
      // Selection changed mid-flight → this result is stale, drop it.
      if (latestLocationRef.current !== target) return;
      setDrafts(snapshotToDrafts(s.rules));
      setLoadedLocationId(target);
    } catch (e) {
      if (latestLocationRef.current === target) setSnapError(formatError(e));
    } finally {
      if (latestLocationRef.current === target) setSnapLoading(false);
    }
  }, [locationId]);
  useEffect(() => { void reload(); }, [reload]);

  // ---- Mutators ----------------------------------------------------------
  const addRule = () => {
    setDrafts((prev) => [
      ...prev,
      {
        draftId:    newDraftId(),
        days:       new Set([2, 3, 5]), // Tue/Wed/Fri default (matches mock)
        validFrom:  `${new Date().getFullYear()}-01-01`,
        validUntil: `${new Date().getFullYear()}-12-31`,
        isOverride: false,
        customDays: false,
        periods:    [{ opens_at: '12:00', closes_at: '17:40' }],
      },
    ]);
  };
  const toggleOverride = (draftId: string) => {
    setDrafts((prev) =>
      prev.map((r) => {
        if (r.draftId !== draftId) return r;
        // Turning an override on: the rule becomes date-driven, so the
        // weekday pattern resets to "every day in the range" (derived at
        // save time). Turning it off: back to a hand-picked weekday rule.
        return r.isOverride
          ? { ...r, isOverride: false, customDays: false }
          : { ...r, isOverride: true, customDays: false, days: new Set(ALL_WEEKDAYS) };
      }),
    );
  };
  const setCustomDays = (draftId: string, custom: boolean) => {
    setDrafts((prev) =>
      prev.map((r) => {
        if (r.draftId !== draftId) return r;
        // Opening the pattern starts from the days that actually occur in
        // the range, so unticking is the only edit the operator has to make.
        const days = custom
          ? weekdaysInRange(r.validFrom, r.validUntil)
          : new Set(ALL_WEEKDAYS);
        return { ...r, customDays: custom, days: days.size > 0 ? days : new Set(ALL_WEEKDAYS) };
      }),
    );
  };
  const removePeriod = (draftId: string, idx: number) => {
    setDrafts((prev) =>
      prev.map((r) =>
        r.draftId === draftId
          ? { ...r, periods: r.periods.filter((_, i) => i !== idx) }
          : r,
      ),
    );
  };
  const addPeriod = (draftId: string) => {
    setDrafts((prev) =>
      prev.map((r) =>
        r.draftId === draftId
          ? { ...r, periods: [...r.periods, { opens_at: '12:00', closes_at: '17:40' }] }
          : r,
      ),
    );
  };
  const removeRule = (draftId: string) => {
    setDrafts((prev) => prev.filter((r) => r.draftId !== draftId));
  };
  const toggleDay = (draftId: string, iso: number) => {
    setDrafts((prev) =>
      prev.map((r) => {
        if (r.draftId !== draftId) return r;
        const days = new Set(r.days);
        if (days.has(iso)) days.delete(iso); else days.add(iso);
        return { ...r, days };
      }),
    );
  };
  const updateRange = (draftId: string, field: 'validFrom' | 'validUntil', value: string) => {
    setDrafts((prev) =>
      prev.map((r) => (r.draftId === draftId ? { ...r, [field]: value } : r)),
    );
  };
  const updatePeriod = (
    draftId: string,
    idx:     number,
    field:   'opens_at' | 'closes_at',
    value:   string,
  ) => {
    setDrafts((prev) =>
      prev.map((r) => {
        if (r.draftId !== draftId) return r;
        const periods = r.periods.map((p, i) => (i === idx ? { ...p, [field]: value } : p));
        return { ...r, periods };
      }),
    );
  };
  const addBreak = (draftId: string) => {
    setDrafts((prev) =>
      prev.map((r) => {
        if (r.draftId !== draftId) return r;
        if (r.periods.length !== 1) return r;
        const only = r.periods[0]!;
        const mid = Math.floor((toMins(only.opens_at) + toMins(only.closes_at)) / 2);
        const breakStart = `${String(Math.floor(mid / 60)).padStart(2, '0')}:${String(mid % 60).padStart(2, '0')}`;
        const breakEnd = `${String(Math.floor((mid + 40) / 60)).padStart(2, '0')}:${String((mid + 40) % 60).padStart(2, '0')}`;
        return {
          ...r,
          periods: [
            { opens_at: only.opens_at, closes_at: breakStart },
            { opens_at: breakEnd,      closes_at: only.closes_at },
          ],
        };
      }),
    );
  };
  const removeBreak = (draftId: string) => {
    setDrafts((prev) =>
      prev.map((r) => {
        if (r.draftId !== draftId) return r;
        if (r.periods.length < 2) return r;
        const first = r.periods[0]!;
        const last  = r.periods[r.periods.length - 1]!;
        return { ...r, periods: [{ opens_at: first.opens_at, closes_at: last.closes_at }] };
      }),
    );
  };

  // ---- Save --------------------------------------------------------------
  const handleSave = useCallback(async () => {
    if (!operator?.id || !locationId || submitting) return;
    // Hard guard: never write drafts that belong to a different location than
    // the one selected — the replace-all save would wipe the target's real
    // schedule and stamp it with the other location's rules.
    if (loadedLocationId !== locationId) return;

    for (let i = 0; i < drafts.length; i++) {
      const r = drafts[i]!;
      const derivedDays = r.isOverride && !r.customDays;
      if (!derivedDays && r.days.size === 0) {
        setSubmitError(`Regel ${i + 1}: kies minstens één weekdag.`);
        return;
      }
      if (!r.validFrom || !r.validUntil) {
        setSubmitError(`Regel ${i + 1}: vul beide datums in.`);
        return;
      }
      if (r.validUntil < r.validFrom) {
        setSubmitError(`Regel ${i + 1}: einddatum mag niet vóór startdatum liggen.`);
        return;
      }
      if (!derivedDays && ruleNeverMatches(r.days, r.validFrom, r.validUntil)) {
        setSubmitError(
          `Regel ${i + 1}: geen van de gekozen weekdagen valt binnen de periode — deze regel zou nooit gelden.`,
        );
        return;
      }
      if (!r.isOverride && r.periods.length === 0) {
        setSubmitError(
          `Regel ${i + 1}: voeg minstens één openingsperiode toe (of markeer als override voor een sluiting).`,
        );
        return;
      }
      let prevClose: string | null = null;
      for (let pi = 0; pi < r.periods.length; pi++) {
        const p = r.periods[pi]!;
        if (toMins(p.closes_at) <= toMins(p.opens_at)) {
          setSubmitError(`Regel ${i + 1}, periode ${pi + 1}: sluitingstijd moet na openingstijd zijn.`);
          return;
        }
        if (prevClose !== null && toMins(p.opens_at) < toMins(prevClose)) {
          setSubmitError(`Regel ${i + 1}, periode ${pi + 1}: overlapt met de vorige periode.`);
          return;
        }
        prevClose = p.closes_at;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: LocationScheduleRuleInput[] = drafts.map((r) => ({
        day_of_week: effectiveDays(r),
        valid_from:  r.validFrom,
        valid_until: r.validUntil,
        is_override: r.isOverride,
        periods:     r.periods.map((p) => ({ opens_at: p.opens_at, closes_at: p.closes_at })),
      }));
      await saveLocationSchedule(operator.id, locationId, payload);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      void reload();
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [operator?.id, locationId, submitting, drafts, reload, loadedLocationId]);

  const totalOpenMinutes = useMemo(
    () => drafts.reduce((sum, r) => sum + ruleOpenMinutes(r), 0),
    [drafts],
  );

  // ---- Render -----------------------------------------------------------
  if (refLoading) return <div className="muted">Laden…</div>;
  if (refError) return <div className="error-card">{refError}</div>;

  return (
    <div className="setup-schedule">
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Locatie</span>
          <select
            value={locationId}
            onChange={(e) => {
              const id = e.target.value;
              setLocationId(id);
              // Keep the global header filter in sync so its follow-effect
              // doesn't fight the dropdown (snap it back / race a double reload).
              setGlobalLocationId(id);
            }}
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.display_name ?? l.name}</option>
            ))}
          </select>
        </label>
        <span className="muted setup-schedule-total">
          Totaal: {fmtDuration(totalOpenMinutes)} per week × dagen
        </span>
      </div>

      <p className="muted setup-schedule-hint">
        Iedere regel bepaalt wanneer een locatie open is, op welke dagen, en in welke periode.
      </p>

      {snapLoading ? <div className="muted">Schema laden…</div> : null}
      {snapError ? <div className="error-card">{snapError}</div> : null}

      {!snapLoading && !snapError ? (
        <>
          {drafts.length === 0 ? (
            <p className="muted setup-rules-empty">
              Nog geen openingsregels. Voeg er minstens één toe.
            </p>
          ) : null}

          {drafts.map((r, i) => (
            <RuleCard
              key={r.draftId}
              index={i}
              rule={r}
              onToggleDay={(iso) => toggleDay(r.draftId, iso)}
              onToggleOverride={() => toggleOverride(r.draftId)}
              onSetCustomDays={(custom) => setCustomDays(r.draftId, custom)}
              onUpdateRange={(field, value) => updateRange(r.draftId, field, value)}
              onUpdatePeriod={(idx, field, value) => updatePeriod(r.draftId, idx, field, value)}
              onAddBreak={() => addBreak(r.draftId)}
              onRemoveBreak={() => removeBreak(r.draftId)}
              onRemovePeriod={(idx) => removePeriod(r.draftId, idx)}
              onAddPeriod={() => addPeriod(r.draftId)}
              onRemove={() => removeRule(r.draftId)}
            />
          ))}

          <button className="btn-link setup-add-rule-btn" onClick={addRule}>
            + Regel toevoegen
          </button>

          {submitError ? (
            <div className="error-card walkin-submit-error">{submitError}</div>
          ) : null}

          <div className="walkin-step-actions">
            <span className="muted">{savedFlash ? '✓ Opgeslagen' : null}</span>
            <button
              className="walkin-primary-btn"
              onClick={() => void handleSave()}
              disabled={
                submitting || !locationId || loadedLocationId !== locationId
              }
            >
              {submitting ? 'Bezig…' : 'Schema opslaan'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function RuleCard({
  index, rule,
  onToggleDay, onToggleOverride, onSetCustomDays, onUpdateRange, onUpdatePeriod,
  onAddBreak, onRemoveBreak, onRemovePeriod, onAddPeriod, onRemove,
}: {
  index:            number;
  rule:             DraftRule;
  onToggleDay:      (iso: number) => void;
  onToggleOverride: () => void;
  onSetCustomDays:  (custom: boolean) => void;
  onUpdateRange:    (field: 'validFrom' | 'validUntil', value: string) => void;
  onUpdatePeriod:   (idx: number, field: 'opens_at' | 'closes_at', value: string) => void;
  onAddBreak:       () => void;
  onRemoveBreak:    () => void;
  onRemovePeriod:   (idx: number) => void;
  onAddPeriod:      () => void;
  onRemove:         () => void;
}) {
  const open = ruleOpenMinutes(rule);
  const hasBreak = rule.periods.length > 1;
  const isClosure = rule.isOverride && rule.periods.length === 0;
  const derivedDays = rule.isOverride && !rule.customDays;
  const singleDate = rule.validFrom !== '' && rule.validFrom === rule.validUntil;
  return (
    <div className="setup-rule-card" data-override={rule.isOverride ? '' : undefined}>
      <div className="setup-rule-card-head">
        <strong>Regel {index + 1}</strong>
        {rule.isOverride ? (
          <span className="hours-badge" style={{ background: '#f8d7da', color: '#842029' }}>
            {isClosure ? 'Sluiting' : 'Afwijkende tijden'}
          </span>
        ) : null}
        <span className="hours-badge">
          {isClosure ? 'Geen openingstijden' : `${fmtDuration(open)} open`}
        </span>
        <button
          className="setup-rule-remove"
          onClick={onRemove}
          aria-label="Regel verwijderen"
          title="Regel verwijderen"
        >
          ✕
        </button>
      </div>

      {/* Type first: it decides what the rest of the card means. */}
      <div className="setup-rule-row">
        <div className="setup-rule-label">Type</div>
        <label className="setup-weekday-pill" title="Vervangt andere regels op dezelfde datums (gebruik voor sluitingen of speciale openingstijden)">
          <input
            type="checkbox"
            checked={rule.isOverride}
            onChange={onToggleOverride}
          />
          <span>Vervang andere regels op deze datums</span>
        </label>
      </div>

      <div className="walkin-row setup-rule-range">
        <label className="walkin-field">
          <span>Vanaf datum</span>
          <input
            type="date"
            value={rule.validFrom}
            onChange={(e) => onUpdateRange('validFrom', e.target.value)}
          />
        </label>
        <label className="walkin-field">
          <span>Tot datum</span>
          <input
            type="date"
            value={rule.validUntil}
            onChange={(e) => onUpdateRange('validUntil', e.target.value)}
          />
        </label>
      </div>

      {derivedDays ? (
        // Override without a hand-picked pattern: the weekdays follow from
        // the dates, so show what the rule applies to instead of asking.
        <div className="setup-rule-row">
          <div className="setup-rule-label">Geldt op</div>
          <div className="setup-rule-derived">
            {singleDate ? (
              <span>{formatDutchDate(rule.validFrom)}</span>
            ) : (
              <>
                <span>Alle dagen in deze periode</span>
                <button className="btn-link" onClick={() => onSetCustomDays(true)}>
                  Alleen bepaalde weekdagen
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="setup-rule-row">
          <div className="setup-rule-label">{rule.isOverride ? 'Geldt op' : 'Open op'}</div>
          <div className="setup-weekday-row">
            {WEEKDAY_ISO.map((iso, i) => (
              <label key={iso} className="setup-weekday-pill">
                <input
                  type="checkbox"
                  checked={rule.days.has(iso)}
                  onChange={() => onToggleDay(iso)}
                />
                <span>{WEEKDAY_LABELS[i]}</span>
              </label>
            ))}
            {rule.isOverride ? (
              <button className="btn-link" onClick={() => onSetCustomDays(false)}>
                Alle dagen
              </button>
            ) : null}
          </div>
        </div>
      )}

      <div className="setup-rule-row">
        <div className="setup-rule-label">Openingstijden</div>
        <div className="setup-rule-periods">
          {isClosure ? (
            <p className="muted">Geen openingstijden — locatie is gesloten op deze datums.</p>
          ) : (
            <>
              {rule.periods.map((p, idx) => (
                <PeriodRow
                  key={idx}
                  kind="open"
                  period={p}
                  showRemove={rule.isOverride}
                  onChange={(field, value) => onUpdatePeriod(idx, field, value)}
                  onRemove={() => onRemovePeriod(idx)}
                />
              ))}
              {hasBreak && rule.periods.length === 2 ? (
                <ClosedGapDisplay
                  opens_at={rule.periods[0]!.closes_at}
                  closes_at={rule.periods[1]!.opens_at}
                  onRemove={onRemoveBreak}
                />
              ) : null}
              {rule.periods.length === 1 ? (
                <button className="btn-link setup-rule-add-break" onClick={onAddBreak}>
                  + Pauze toevoegen
                </button>
              ) : null}
            </>
          )}
          {rule.isOverride ? (
            <button className="btn-link setup-rule-add-break" onClick={onAddPeriod}>
              + Openingstijd toevoegen
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PeriodRow({
  kind, period, showRemove = false, onChange, onRemove,
}: {
  kind: 'open' | 'closed';
  period: DraftPeriod;
  showRemove?: boolean;
  onChange: (field: 'opens_at' | 'closes_at', value: string) => void;
  onRemove?: () => void;
}) {
  return (
    <div className={`setup-period-row setup-period-${kind}`}>
      <span className={`setup-period-label setup-period-label-${kind}`}>
        {kind === 'open' ? 'Open' : 'Pauze'}
      </span>
      <input
        type="time"
        step={60}
        value={period.opens_at}
        onChange={(e) => onChange('opens_at', e.target.value)}
        className="setup-period-time"
      />
      <span className="setup-period-arrow">→</span>
      <input
        type="time"
        step={60}
        value={period.closes_at}
        onChange={(e) => onChange('closes_at', e.target.value)}
        className="setup-period-time"
      />
      {showRemove && onRemove ? (
        <button
          className="btn-link setup-period-remove"
          onClick={onRemove}
          title="Periode verwijderen"
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

function ClosedGapDisplay({
  opens_at, closes_at, onRemove,
}: {
  opens_at:  string;
  closes_at: string;
  onRemove:  () => void;
}) {
  return (
    <div className="setup-period-row setup-period-closed">
      <span className="setup-period-label setup-period-label-closed">Pauze</span>
      <span className="setup-period-time-display">{opens_at}</span>
      <span className="setup-period-arrow">→</span>
      <span className="setup-period-time-display">{closes_at}</span>
      <button
        className="btn-link setup-period-remove"
        onClick={onRemove}
        title="Pauze verwijderen"
      >
        ✕
      </button>
    </div>
  );
}
